/* neekie landing page — behaviour
   The design is a 1440px-wide stage that is scaled to the viewport width. */
(() => {
  'use strict';

  const DESIGN_W = 1440;
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));

  if (/[?&]static\b/.test(location.search)) document.documentElement.classList.add('static');

  let scale = 1;
  const MOBILE_MAX = 767;

  function isMobile() {
    return (document.documentElement.clientWidth || window.innerWidth) <= MOBILE_MAX;
  }

  /* ---- Fit the 1440 design to the viewport (every section reads --s) ------- */
  function fit() {
    const mobile = isMobile();
    document.documentElement.classList.toggle('is-mobile', mobile);
    /* On mobile, top sections use dedicated layout; rest still scales from 390 design feel */
    scale = mobile ? Math.max((document.documentElement.clientWidth || window.innerWidth) / DESIGN_W, 0.22) : (document.documentElement.clientWidth || window.innerWidth) / DESIGN_W;
    document.documentElement.style.setProperty('--s', scale);
    onScroll();
  }

  /* ---- Nav: sits at y=65 (as designed) and tucks up as you scroll ---------- */
  function onScroll() {
    if (isMobile()) return;
    const y = window.scrollY / scale;             // scroll distance in design px
    const nav = $('#nav');
    if (!nav) return;
    nav.style.top = `${Math.max(16, 65 - y)}px`;
    nav.classList.toggle('scrolled', y > 40);
  }

  /* ---- Mobile menu drawer -------------------------------------------------- */
  function setupMobileNav() {
    const toggle = $('#mNavToggle');
    const drawer = $('#mDrawer');
    const scrim = $('#mDrawerScrim');
    if (!toggle || !drawer) return;

    const setOpen = (open) => {
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      toggle.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
      document.body.classList.toggle('m-nav-open', open);
      if (open) {
        drawer.hidden = false;
        if (scrim) scrim.hidden = false;
        requestAnimationFrame(() => drawer.classList.add('is-open'));
      } else {
        drawer.classList.remove('is-open');
        setTimeout(() => {
          if (!drawer.classList.contains('is-open')) {
            drawer.hidden = true;
            if (scrim) scrim.hidden = true;
          }
        }, 360);
      }
    };

    toggle.addEventListener('click', () => {
      setOpen(toggle.getAttribute('aria-expanded') !== 'true');
    });
    scrim && scrim.addEventListener('click', () => setOpen(false));
    drawer.querySelectorAll('a').forEach((a) => a.addEventListener('click', () => setOpen(false)));
  }

  /* ---- Marquees: shift by exactly one repeated set so the loop is seamless -- */
  function buildRibbons() {
    // Each ribbon is one phrase set repeated; ✦ is drawn as an SVG so it looks the same on every OS.
    $$('.ribbon .track[data-set]').forEach((track) => {
      const html = track.dataset.set.replace(/✦/g, '<i class="st"></i>');
      track.innerHTML = Array(4).fill(`<span class="set">${html}</span>`).join('');
    });
  }

  function setupMarquees() {
    $$('.ribbon .track, #announce .track').forEach((track) => {
      const set = $('.set', track);
      if (set && set.offsetWidth) track.style.setProperty('--shift', `${set.offsetWidth}px`);
    });
  }

  /* ---- Scroll reveal ------------------------------------------------------- */
  function setupReveal() {
    const items = $$('[data-r]');
    // ?static skips the entrance animations (handy for screenshots / visual diffs)
    if (!('IntersectionObserver' in window) || /[?&]static\b/.test(location.search)) { items.forEach((el) => el.classList.add('in')); return; }
    let fired = false;
    const io = new IntersectionObserver((entries) => {
      fired = true;
      entries.forEach((e) => {
        if (e.isIntersecting || e.boundingClientRect.top < 0) {
          e.target.classList.add('in');
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -6% 0px' });
    items.forEach((el) => io.observe(el));

    // Safety net: if the observer never reports (throttled / embedded browsers), reveal by
    // position on scroll, and reveal everything after a few seconds — never leave content hidden.
    let ticking = false;
    const sweep = () => {
      ticking = false;
      const vh = window.innerHeight;
      items.forEach((el) => {
        if (el.classList.contains('in')) return;
        if (el.getBoundingClientRect().top < vh * 0.94) el.classList.add('in');
      });
    };
    window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(sweep); } }, { passive: true });
    setTimeout(() => { if (!fired) items.forEach((el) => el.classList.add('in')); }, 3000);
  }

  /* ---- Shop by: Occasion / Climate tabs ------------------------------------- */
  function setupTabs() {
    const tabs = $('#tabs');
    if (tabs) {
      const pill = $('.pill', tabs);
      const buttons = $$('button[role="tab"]', tabs);
      buttons.forEach((b) => b.addEventListener('click', () => {
        buttons.forEach((x) => x.setAttribute('aria-selected', String(x === b)));
        pill.style.left = `${b.dataset.l}px`;
        pill.style.width = `${b.dataset.w}px`;
      }));
    }

    const mTabs = $('#mTabs');
    if (mTabs) {
      const mButtons = $$('button[role="tab"]', mTabs);
      mButtons.forEach((b) => b.addEventListener('click', () => {
        const i = b.dataset.tab || '0';
        mTabs.setAttribute('data-active', i);
        mButtons.forEach((x) => {
          const on = x === b;
          x.setAttribute('aria-selected', String(on));
          x.classList.toggle('is-active', on);
        });
      }));
    }
  }

  /* ---- Testimonial carousel -------------------------------------------------- */
  function setupCarousel() {
    const track = $('#tTrack');
    if (track) {
      const PITCH = 796;
      const originals = $$('[data-slide]', track);
      originals.forEach((s) => {
        const c = s.cloneNode(true);
        c.style.left = `${parseFloat(s.style.left) + PITCH * 2}px`;
        c.setAttribute('aria-hidden', 'true');
        track.appendChild(c);
      });
      const bars = $$('#progress i');
      const prev = $('#tPrev');
      const next = $('#tNext');
      const last = bars.length - 1;
      let idx = 0;
      function go(n) {
        idx = (n + last + 1) % (last + 1);
        track.style.transform = `translateX(${-PITCH * idx}px)`;
        bars.forEach((b, i) => b.classList.toggle('on', i === idx));
      }
      if (prev) prev.addEventListener('click', () => go(idx - 1));
      if (next) next.addEventListener('click', () => go(idx + 1));
      bars.forEach((b, i) => { b.style.cursor = 'pointer'; b.addEventListener('click', () => go(i)); });
      go(0);
    }

    const mTrack = $('#mReviewsTrack');
    if (mTrack) {
      const cards = $$('[data-m-slide]', mTrack);
      const bars = $$('#mReviewsBars i');
      const prev = $('#mReviewsPrev');
      const next = $('#mReviewsNext');
      const last = Math.max(cards.length - 1, 0);
      let idx = 0;
      let ticking = false;

      function step() {
        const c = cards[0];
        if (!c) return 280;
        const gap = parseFloat(getComputedStyle(mTrack).columnGap || getComputedStyle(mTrack).gap) || 12;
        return c.getBoundingClientRect().width + gap;
      }

      function syncBars(i) {
        idx = Math.max(0, Math.min(last, i));
        bars.forEach((b, j) => b.classList.toggle('is-on', j === idx));
      }

      function readIdx() {
        return Math.round(mTrack.scrollLeft / Math.max(step(), 1));
      }

      if (prev) prev.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const i = readIdx();
        if (i <= 0) {
          mTrack.scrollTo({ left: last * step(), behavior: 'smooth' });
          syncBars(last);
        } else {
          mTrack.scrollBy({ left: -step(), behavior: 'smooth' });
          syncBars(i - 1);
        }
      });
      if (next) next.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const i = readIdx();
        if (i >= last) {
          mTrack.scrollTo({ left: 0, behavior: 'smooth' });
          syncBars(0);
        } else {
          mTrack.scrollBy({ left: step(), behavior: 'smooth' });
          syncBars(i + 1);
        }
      });

      mTrack.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          syncBars(readIdx());
          ticking = false;
        });
      }, { passive: true });

      syncBars(0);
    }
  }

  /* ---- Newsletter --------------------------------------------------------------
     Front-end only: wire `submitEmail` to your email provider / API. */
  function setupNewsletter() {
    const form = $('#neekieNewsletter');
    const input = $('#emailField');
    if (!form || !input) return;
    form.addEventListener('submit', () => {
      if (!input.checkValidity() || !input.value) {
        input.focus();
        input.reportValidity();
      }
    });
  }

  /* ---- Video: keyboard access (attach a video source here when you have one) - */
  function setupVideo() {
    const v = $('#video');
    if (!v) return;
    v.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); v.click(); } });
  }

  /* ---- Keep placeholder links from jumping to the top ------------------------- */
  document.addEventListener('click', (e) => {
    const a = e.target.closest('a[href="#"]');
    if (a) e.preventDefault();
  });

  /* ---- Anchor scrolling in scaled space ---------------------------------------- */
  document.addEventListener('click', (e) => {
    const a = e.target.closest('a[href^="#"]:not([href="#"])');
    if (!a) return;
    const target = document.querySelector(a.getAttribute('href'));
    if (!target) return;
    e.preventDefault();
    const y = target.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({ top: y - 90 * scale, behavior: 'smooth' });
  });

  window.addEventListener('resize', fit);
  window.addEventListener('scroll', onScroll, { passive: true });

  fit();
  setupMobileNav();
  setupTabs();
  setupCarousel();
  setupNewsletter();
  setupVideo();
  setupReveal();
  buildRibbons();
  (document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve()).then(setupMarquees);
})();

/* ---- Add to cart → Impact right cart drawer (desktop + mobile) ------------ */
(function () {
  'use strict';

  function rootPath() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  }

  function updateCartBadges(count) {
    document.querySelectorAll('.nkh-cart').forEach(function (a) {
      var b = a.querySelector('.nkh-cart__count');
      if (!b) {
        b = document.createElement('span');
        b.className = 'nkh-cart__count';
        a.appendChild(b);
      }
      b.textContent = String(count);
    });
    document.querySelectorAll('cart-count').forEach(function (el) {
      el.textContent = String(count);
    });
  }

  function openCartDrawer() {
    var drawer = document.getElementById('cart-drawer');
    if (!drawer || typeof drawer.show !== 'function') return;
    if (drawer.open) return;
    drawer.show();
  }

  window.nkAddToCart = function (form, opts) {
    opts = opts || {};
    var root = rootPath();
    var sectionsToBundle = ['variant-added'];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle }
      })
    );

    var formData = new FormData(form);
    formData.set('sections', sectionsToBundle.join(','));
    var idInput = form.querySelector('[name="id"]');
    if (idInput && idInput.value) {
      formData.set('sections_url', root + 'variants/' + idInput.value);
    }

    return fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: formData
    })
      .then(function (r) {
        return r.json().then(function (j) {
          if (!r.ok) throw new Error(j.description || j.message || 'Could not add to cart');
          return j;
        });
      })
      .then(function (responseJson) {
        return fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            cart.sections = responseJson.sections || {};
            updateCartBadges(cart.item_count);

            var cartType = window.themeVariables && themeVariables.settings && themeVariables.settings.cartType;
            if (!opts.blockDrawer && cartType === 'page') {
              window.location.href = root + 'cart';
              return cart;
            }

            var hasDrawerSection = false;
            try {
              var drawerEl = document.getElementById('cart-drawer');
              if (drawerEl && cart.sections) {
                var sid = drawerEl.closest('.shopify-section');
                if (sid && sid.id) {
                  var key = sid.id.replace('shopify-section-', '');
                  hasDrawerSection = !!cart.sections[key];
                }
              }
            } catch (e) {}

            var detail = {
              items: responseJson.items || [responseJson],
              cart: cart,
              blockCartDrawerOpening: opts.blockDrawer === true
            };

            document.documentElement.dispatchEvent(
              new CustomEvent('variant:add', { bubbles: true, detail: detail })
            );

            if (hasDrawerSection) {
              document.documentElement.dispatchEvent(
                new CustomEvent('cart:change', {
                  bubbles: true,
                  detail: { baseEvent: 'variant:add', cart: cart }
                })
              );
            } else {
              // Refresh drawer HTML from section endpoint, then open
              document.dispatchEvent(new CustomEvent('cart:refresh'));
            }

            document.dispatchEvent(new CustomEvent('nk:cart:updated', { detail: cart }));

            if (!opts.blockDrawer) {
              // Open after a tick so refresh/replace can settle
              setTimeout(openCartDrawer, hasDrawerSection ? 30 : 180);
            }
            return cart;
          });
      });
  };

  window.nkOpenCartDrawer = openCartDrawer;
})();
